import React, { Fragment } from 'react';
import {
  Block,
  Button,
  Card,
  Col,
  Icon,
  Preloader,
  Row
} from 'framework7-react';

import II18 from '../../translate/II18';
import { gae, gaPageView } from '../../helpers/Analytics';
import PropTypes from 'prop-types';
import {
  getIcon,
  ID_CHECK_DOC_TYPE_ICONS,
  ID_CHECK_STEP_DOC_BACK,
  ID_CHECK_STEP_DOC_FRONT,
  ID_CHECK_STEP_DOC_LIVE,
  ID_CHECK_STEP_DOC_TYPE,
  ID_CHECK_STEP_SELFIE,
  MODE_CAMERA_BACK,
  MODE_CAMERA_FRONT,
  MODE_PHOTO,
  prepareScanUtils
} from '../../helpers/utils';
import Uploader from '../../common/Uploader';
import ImageViewer from '../../gallery/desktop/ImageViewer';
import withDocumentCommonUI from '../../helpers/withDocumentCommonUI';
import { SuccessMessageBlock } from '../../requestedDocuments/Elements';
import {
  handleCameraStartError,
  queryCameraPermissionState
} from '../../helpers/cameraPermission';

require('../../../lib/scanutils');

class IdCheckContent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showPreview: false,
      cameraStarting: false,
      pendingCameraDocType: null
    };
    this.cameraReadyListener = null;
  }

  componentDidMount() {
    prepareScanUtils();

    gaPageView('Id Check');
    document.body.classList.add('idcheck');
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      this.props.cameraIsOnDocType &&
      this.props.idCheckSteps[this.props.activeStep]?.id !==
        ID_CHECK_STEP_SELFIE
    ) {
      this.doCancelCamera();
      return;
    }

    const { pendingCameraDocType } = this.state;
    if (
      pendingCameraDocType &&
      this.props.cameraIsOnDocType === pendingCameraDocType &&
      document.getElementById('selfie')
    ) {
      this.setState({ pendingCameraDocType: null }, () => {
        this.mountAndStartCamera(pendingCameraDocType);
      });
    }
  }

  componentWillUnmount() {
    document.body.classList.remove('idcheck');
    this.clearCameraReadyListener();
    window.utils.stopCamera();
  }

  showPreview(imageUrl) {
    this.setState({
      showPreview: imageUrl
    });
  }

  doLoad = pageId => {
    gae('IDCHECK', 'START_PASS_MODE');
    Uploader.loadFromFile(
      false,
      urls => {
        if (window.utils.isLoadedImageTooSmall(500)) {
          this.$f7.dialog.alert(
            II18.get('cropper_is_to_small_message'),
            II18.get('dialog_warning')
          );
        } else {
          this.savePage(urls[0], pageId);
          this.forceUpdate();
        }
      },
      () => this.$f7.dialog.progress(II18.get('title_load'), 0)
    );
  };

  savePage = (url, actionPage) => {
    this.props.setDocumentDataUrl(this.props.document.id, actionPage, url);
    this.forceUpdate();
  };

  clearCameraReadyListener = () => {
    this.cameraReadyListener = null;
  };

  markCameraReady = () => {
    this.clearCameraReadyListener();
    this.setState({ cameraStarting: false });
  };

  mountAndStartCamera = docType => {
    this.clearCameraReadyListener();

    window.utils.createCamera(
      'selfie',
      '',
      docType === ID_CHECK_STEP_SELFIE,
      false,
      notAllowed => {
        this.clearCameraReadyListener();
        this.setState({ cameraStarting: false });
        handleCameraStartError(this.$f7, notAllowed, {
          analyticsCategory: 'IDCHECK',
          errorMessageKey: 'camera_error_message2',
          onRetry: notAllowed ? () => this.startCameraMode(docType) : undefined,
          onClose: () => this.props.setCameraIsOnDocType(false)
        });
      }
    );

    const startOptions = {
      skipSpinner: true,
      onReady: () => this.markCameraReady()
    };

    if (docType === ID_CHECK_STEP_SELFIE) {
      window.utils.startCameraIdeal(startOptions);
    } else {
      window.utils.startCamera(true, false, startOptions);
    }
  };

  startCameraMode = docType => {
    gae('IDCHECK', 'START_CAMERA_MODE');

    const beginCapture = () => {
      this.setState({
        cameraStarting: true,
        pendingCameraDocType: docType
      });
      this.props.setCameraIsOnDocType(docType);
    };

    queryCameraPermissionState().then(state => {
      if (state === 'denied') {
        handleCameraStartError(this.$f7, true, {
          analyticsCategory: 'IDCHECK',
          onRetry: () => this.startCameraMode(docType),
          onClose: () => this.props.setCameraIsOnDocType(false)
        });
        return;
      }

      if (state === 'granted') {
        beginCapture();
        return;
      }

      this.$f7.dialog
        .create({
          title: II18.get('camera_disabled_title'),
          text: II18.get('camera_permission_preprompt'),
          buttons: [
            {
              text: II18.get('title_cancel'),
              onClick: () => this.props.setCameraIsOnDocType(false)
            },
            {
              text: II18.get('camera_permission_continue'),
              bold: true,
              cssClass: 'main-button',
              onClick: () => {
                gae('IDCHECK', 'CAMERA_PERMISSION_PREPROMPT_CONTINUE');
                beginCapture();
              }
            }
          ],
          verticalButtons: !window.isDesktop
        })
        .open();
    });
  };

  doShot = () => {
    if (this.state.cameraStarting) {
      return;
    }
    if (window.utils.shotCamera()) {
      const url = window.utils.processLoadedImageToResize();

      window.utils.stopCamera();
      this.savePage(url, this.props.cameraIsOnDocType);
      this.props.setCameraIsOnDocType(false);
      this.setState({ cameraStarting: false, pendingCameraDocType: null });
    }
  };

  doCancelCamera = () => {
    this.clearCameraReadyListener();
    window.utils.stopCamera();
    this.props.setCameraIsOnDocType(false);
    this.setState({ cameraStarting: false, pendingCameraDocType: null });
  };

  removePage = pageId => {
    this.props.removeDocumentPage(this.props.document.id, pageId);
  };

  infoIcon(icon, label) {
    return (
      <Col style={{ position: 'relative', textAlign: 'center' }}>
        <Icon className={'idCheckInfoIconBadge'} material={'check_circle'} />
        <Card
          className={'idCheckInfoIconCard'}
          content={<span className={'idCheckInfoIcon ' + icon} />}
        />
        <span style={{ padding: 10, display: 'block' }}>{label}</span>
      </Col>
    );
  }

  showPassedPhotoStep = step => {
    return (
      <div className={'step-content'}>
        <div className={'idCheckPreview'}>
          <div></div>
          <img
            className={'pageId-' + step.id}
            alt={step.id}
            style={{ cursor: 'zoom-in' }}
            src={step.url}
            onClick={() => this.showPreview(step.url)}
          />
          <div>
            {!this.props.scanEnded && (
              <Button
                onClick={e => {
                  e.stopPropagation();
                  this.removePage(step.id);
                }}
                className={'id_check_delete_page on_hover_title'}
                fill
                raised
                color={'red'}
              >
                <Icon material={'delete_forever'} />
                <span>{II18.get('title_delete')}</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  uploadDataBlock = (pageId, btnTitle, btnId, btnIcon, icon, onDataLoad) => {
    return (
      <div className={'step-content pageId-' + pageId}>
        <div className={'step-upload-block'}>
          <div className={'step-info-icon'} onClick={onDataLoad}>
            {icon}
          </div>
          <Button
            id={btnId}
            large
            raised
            fill
            style={{ marginBottom: 10 }}
            onClick={onDataLoad}
          >
            {btnIcon} {btnTitle}
          </Button>
        </div>
      </div>
    );
  };

  uploadPhotoBlock = pageId => {
    return this.uploadDataBlock(
      pageId,
      II18.get('upload_disk'),
      'upload_image',
      '',
      getIcon(
        pageId === ID_CHECK_STEP_DOC_BACK
          ? 'fa-credit-card'
          : ID_CHECK_DOC_TYPE_ICONS[this.props.idCheckDocumentType],
        100
      ),
      () => this.doLoad(pageId)
    );
  };

  livePhotoBlock = () => {
    return this.uploadDataBlock(
      ID_CHECK_STEP_DOC_LIVE,
      II18.get('idcheck_start_camera'),
      'idcheck_start_camera',
      getIcon('material:videocam'),
      <img alt={'Selfie'} src={'/pers.jpg'} width={200} />,
      () => this.startCameraMode(ID_CHECK_STEP_DOC_LIVE)
    );
  };

  selfiePhotoBlock = () => {
    return this.uploadDataBlock(
      ID_CHECK_STEP_SELFIE,
      II18.get('idcheck_start_camera'),
      'idcheck_start_camera',
      getIcon('material:videocam'),
      getIcon('material:person', 100),
      () => this.startCameraMode(ID_CHECK_STEP_SELFIE)
    );
  };

  /*stepContainer(number, title, last) {
    const active = number === this.props.activeStep + 1;
    const done = number < this.props.lastNotReadyStep + 1;
    const clickable = number <= this.props.lastNotReadyStep + 1;
    return (
      <Col
        onClick={
          clickable ? () => this.setState({ activeStep: number - 1 }) : null
        }
        key={number}
        className={
          'stepContainer' +
          (active ? ' active' : '') +
          (done ? ' done' : '') +
          (clickable ? ' clickable' : '') +
          (last ? ' last' : '')
        }
      >
        <label className={'stepLine'} />
        <label className={'stepNumber'}>{number}</label>
        <label className={'stepText'}>{title}</label>
      </Col>
    );
  }*/

  showStepContent(step) {
    switch (step.id) {
      case ID_CHECK_STEP_DOC_TYPE:
        return (
          <div className={'select_doc_type'}>
            {this.props.possibleDocuments.map(docType =>
              this.idDocumentTypeSelect(docType)
            )}
          </div>
        );
      default:
        return step.passed
          ? this.showPassedPhotoStep(step)
          : this.showPhotoStep(step);
    }
  }

  showPhotoStep(step) {
    switch (step.loadMode) {
      case MODE_PHOTO:
        return this.uploadPhotoBlock(
          step.id,
          step.documentTitle,
          step.id === ID_CHECK_STEP_DOC_FRONT
        );
      case MODE_CAMERA_FRONT:
        return this.selfiePhotoBlock();
      case MODE_CAMERA_BACK:
        return this.livePhotoBlock();
      //no default
    }
  }

  idDocumentTypeSelect = docType => (
    <Button
      tabIndex={0}
      key={docType}
      className={
        'doc_type type_' +
        docType +
        (this.props.idCheckDocumentType === docType ? ' selected' : '')
      }
      onClick={() => {
        if (!this.props.scanEnded) {
          this.props.setIdCheckDocumentType(this.props.document.id, docType);
          document.getElementById('stepNext')?.focus();
        }
      }}
    >
      <span className={'doc_type_icon'}>
        {getIcon(ID_CHECK_DOC_TYPE_ICONS[docType], 45)}
      </span>
      <span className={'doc_type_title'}>
        {II18.get('id_check_doc_type_' + docType)}
      </span>
    </Button>
  );

  showCameraContent = () => {
    const { cameraStarting } = this.state;

    return (
      <div className={'step-content'} style={{ position: 'relative' }}>
        <div
          id={'selfie'}
          style={{
            flexGrow: 1,
            width: '100%',
            textAlign: 'center',
            overflow: 'hidden',
            marginTop: 10,
            display: 'grid',
            minHeight: 280
          }}
        />
        {cameraStarting && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 1100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
              background: 'rgba(255, 255, 255, 0.92)',
              textAlign: 'center'
            }}
          >
            <Preloader size={42} />
            <p style={{ maxWidth: 360, lineHeight: 1.45, marginTop: 16 }}>
              {II18.get('camera_starting_message')}
            </p>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 10,
            justifyContent: 'center'
          }}
        >
          {!cameraStarting && (
            <Button
              id={'idcheck_take_photo'}
              outline
              raised
              round
              style={{
                position: 'absolute',
                zIndex: 1000,
                marginTop: -90,
                background: 'rgba(255,255,255,.7)',
                opacity: 0.9
              }}
              onClick={this.doShot}
            >
              <Icon size={24} f7={'camera'} /> <II18>idcheck_take_photo</II18>
            </Button>
          )}
        </div>
      </div>
    );
  };

  render = () => {
    const {
      idCheckSteps,
      cameraIsOnDocType,
      activeStep: activeStepIndex,
      moveToStep
    } = this.props;

    const { showPreview } = this.state;

    const activeStepInfo = idCheckSteps[activeStepIndex];

    return (
      <div id={'idCheckContent'}>
        <ImageViewer
          startPage={0}
          pages={[showPreview]}
          onClose={() => this.showPreview(false)}
          opened={Boolean(showPreview)}
        />

        {/*<div
          className={'id_check_steps'}
          style={{ display: 'flex', padding: 10 }}
        >
          <Row style={{ flexGrow: 1, justifyContent: 'start' }}>
            {idCheckSteps.map((step, index) =>
              this.stepContainer(index + 1, step.stepTitle)
            )}
          </Row>
          <div style={{ flexGrow: 0 }}>
            {this.stepContainer(idCheckSteps.length + 1, 'Finish', true)}
          </div>
        </div>*/}

        {activeStepInfo && (
          <>
            <h3
              className={
                'padding-left step-title' +
                (activeStepInfo.passed &&
                activeStepInfo.id !== ID_CHECK_STEP_DOC_TYPE
                  ? ' step-done'
                  : '')
              }
              style={{
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              {activeStepInfo.passed &&
              activeStepInfo.id !== ID_CHECK_STEP_DOC_TYPE
                ? activeStepInfo.documentTitle
                : activeStepInfo.documentShortDesc}
              {activeStepInfo.passed &&
                activeStepInfo.id !== ID_CHECK_STEP_DOC_TYPE &&
                getIcon('f7:checkmark_alt_circle')}
            </h3>
            {cameraIsOnDocType
              ? this.showCameraContent(activeStepIndex)
              : this.showStepContent(activeStepInfo)}
          </>
        )}

        {activeStepIndex === idCheckSteps.length && (
          <Block className={'id_check_result_gallery idCheck_done'}>
            <div
              style={{
                padding: 10,
                background: 'rgb(227 242 249)',
                borderRadius: 10,
                marginBottom: 10
              }}
            >
              {II18.get('idcheck_summary')}
            </div>
            {this.props.scannedPages?.size > 0 ? (
              <Row style={{ flexWrap: 'nowrap' }}>
                {idCheckSteps.map(
                  (step, index) =>
                    step.url && (
                      <Col key={step.id} className={'text-align-center'}>
                        <h4
                          style={{ minHeight: 25, cursor: 'pointer' }}
                          onClick={() => moveToStep(index)}
                        >
                          {step.documentTitle}
                        </h4>
                        <div className={'result_image'}>
                          <img
                            className={'pageId-' + step.id}
                            alt={step.id}
                            width={'100%'}
                            src={step.url}
                            onClick={() => this.showPreview(step.url)}
                          />
                        </div>
                      </Col>
                    )
                )}
              </Row>
            ) : (
              <SuccessMessageBlock
                submittedDate={this.props.submittedDate}
                oneDocument
              />
            )}
          </Block>
        )}
      </div>
    );
  };
}

IdCheckContent.propTypes = {
  moveToStep: PropTypes.func,
  scannedPages: PropTypes.object,
  cameraIsOnDocType: PropTypes.any,
  setCameraIsOnDocType: PropTypes.func,
  activeStep: PropTypes.number,
  possibleDocuments: PropTypes.array,
  document: PropTypes.object,
  setIdCheckDocumentType: PropTypes.func,
  setDocumentDataUrl: PropTypes.func,
  removeDocumentPage: PropTypes.func,
  idCheckDocumentType: PropTypes.string,
  submittedDate: PropTypes.string,
  idCheckSteps: PropTypes.array,
  scanEnded: PropTypes.bool
};

export default withDocumentCommonUI(IdCheckContent);
